import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import DataTable, { type ExpanderComponentProps, type TableColumn } from "react-data-table-component";
import styles from "./UnusedEntitlement.module.css";
import panel from "../admin/AdminPanel.module.css";
import adminStyles from "../admin/Admin.module.css";
import { adminTableTheme } from "../admin/adminTableTheme";
import { TablePager } from "../shared/TablePager";
import { useColumnFilters, type ColumnFilterSpec } from "../shared/columnFilters";
import { formatMoney } from "../consumption/formatters";
import { formatMonth } from "./formatMonth";
import type { UnattributedConsumptionEntryResponse } from "../../api/organisationReporting";

/**
 * Spend no person carries (`TD-19`): provider accounts the sync could not tie to a person, such as a Claude API user, kept in their own
 * category rather than dropped or folded into anyone else's figures. A sortable, filterable, paged table: each account's spend in US
 * dollars and the months it was used in, opening to its figures month by month.
 *
 * @param props.entries - Every unattributed account.
 * @param props.exportAction - The CSV export button, for the panel's header.
 */
export function UnattributedConsumptionSection({ entries, exportAction }: { entries: UnattributedConsumptionEntryResponse[]; exportAction?: React.ReactNode }): React.JSX.Element {
  const { t, i18n } = useTranslation();

  const queueLabel = useMemo(
    () => (entry: UnattributedConsumptionEntryResponse): string => (entry.hasPendingIdentityQueueEntry ? t("unattributed.pendingInQueue") : t("unattributed.notInQueue")),
    [t],
  );

  const specs = useMemo<ColumnFilterSpec<UnattributedConsumptionEntryResponse>[]>(
    () => [
      { id: "tool", kind: "set", value: (entry) => entry.vendorDisplayName },
      { id: "account", kind: "text", value: (entry) => entry.vendorNativeId },
      { id: "queue", kind: "set", value: queueLabel },
    ],
    [queueLabel],
  );
  const { filtered, heading } = useColumnFilters(entries, specs);

  const columns = useMemo<TableColumn<UnattributedConsumptionEntryResponse>[]>(
    () => [
      { id: "tool", name: heading("tool", t("unattributed.table.vendor")), selector: (entry) => entry.vendorDisplayName, sortable: true },
      { id: "account", name: heading("account", t("unattributed.table.identity")), selector: (entry) => entry.vendorNativeId, sortable: true, grow: 2, wrap: true },
      {
        id: "spend",
        name: t("unattributed.table.value"),
        selector: (entry) => entry.totalCostUsd ?? -1,
        sortable: true,
        right: true,
        format: (entry) => (entry.totalCostUsd !== null ? formatMoney(entry.totalCostUsd, "USD") : t("unattributed.valueNotConverted")),
      },
      {
        id: "used",
        name: t("unattributed.table.used"),
        selector: (entry) => entry.lastUsedMonth ?? "",
        sortable: true,
        grow: 2,
        wrap: true,
        format: (entry) =>
          entry.lastUsedMonth === null
            ? t("unattributed.neverUsed")
            : entry.lastUsedMonth === entry.firstMonth
              ? formatMonth(entry.lastUsedMonth, i18n.language)
              : t("unattributed.usedFromTo", { from: formatMonth(entry.firstMonth, i18n.language), to: formatMonth(entry.lastUsedMonth, i18n.language) }),
      },
      { id: "queue", name: heading("queue", t("unattributed.table.queueStatus")), selector: queueLabel, sortable: true, grow: 2, wrap: true },
    ],
    [heading, queueLabel, t, i18n.language],
  );

  return (
    <section className={panel.panel} aria-labelledby="unattributed-heading">
      <div className={panel.header}>
        <h2 id="unattributed-heading" className={`panel-title ${panel.title}`}>
          {t("unattributed.title")}
          <span className={styles.count}>{entries.length.toLocaleString(i18n.language)}</span>
        </h2>
        {exportAction}
      </div>
      <p className={panel.intro}>{t("unattributed.explanation")}</p>
      {entries.length === 0 ? (
        <p className={styles.clear}>{t("unattributed.none")}</p>
      ) : (
        <div className={`${adminStyles.tableWrapper} ${adminStyles.peopleTableWrapper}`}>
          <DataTable
            persistTableHead
            columns={columns}
            data={filtered}
            className={adminStyles.peopleDataTable}
            keyField="vendorSubjectId"
            ariaLabel={t("unattributed.title")}
            theme={adminTableTheme}
            defaultSortFieldId="used"
            defaultSortAsc={false}
            pagination
            paginationComponent={TablePager}
            paginationPerPage={25}
            paginationRowsPerPageOptions={[25, 50, 100]}
            expandableRows
            expandableRowsComponent={AccountMonths}
            noDataComponent={<p className={adminStyles.note}>{t("unusedEntitlement.filters.noMatches")}</p>}
            striped
            highlightOnHover
            columnSeparator="subtle"
          />
        </div>
      )}
    </section>
  );
}

/** One account's figures month by month, newest first, under its row. */
function AccountMonths({ data }: ExpanderComponentProps<UnattributedConsumptionEntryResponse>): React.JSX.Element {
  const { t, i18n } = useTranslation();

  return (
    <div className={styles.expanded}>
      <table className={styles.monthsTable}>
        <caption className="visually-hidden">{t("unattributed.monthsCaption", { account: data.vendorNativeId })}</caption>
        <thead>
          <tr>
            <th scope="col">{t("unattributed.table.month")}</th>
            <th scope="col" className={styles.number}>
              {t("unattributed.table.monthSpend")}
            </th>
            <th scope="col" className={styles.number}>
              {t("unattributed.table.requests")}
            </th>
          </tr>
        </thead>
        <tbody>
          {[...data.months].reverse().map((month) => (
            <tr key={month.periodStart}>
              <td>{formatMonth(month.periodStart, i18n.language)}</td>
              <td className={styles.number}>{month.costUsd !== null ? formatMoney(month.costUsd, "USD") : t("unattributed.valueNotConverted")}</td>
              <td className={styles.number}>{month.requests.toLocaleString(i18n.language)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
